import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import {
  BaseView,
  DateTzPipe,
  IPaginatedResponse,
  ListRequestSortOrderEnum,
  TranslatePipe,
} from 'O3_core';
import { IEmailQueue } from '../email-queue/interfaces/email-queue.interface';
import { ISmsQueue } from '../sms-queue/interfaces/sms-queue.interface';
import {
  EmailQueueService,
  IEmailQueueStats,
} from '../email-queue/services/email-queue.service';
import { IEmailStatus } from '../email-queue/interfaces/email-status.interface';
import { EmailStatusService } from '../email-queue/services/email-status.service';
import {
  SmsQueueService,
  ISmsQueueStats,
} from '../sms-queue/services/sms-queue.service';
import { ISmsStatus } from '../sms-queue/interfaces/sms-status.interface';
import { SmsStatusService } from '../sms-queue/services/sms-status.service';

interface IDashboardStats {
  emailPending: number;
  smsPending: number;
  failedToday: number;
  deliveredToday: number;
}

@Component({
  selector: 'o3-notifications-dashboard',
  imports: [CommonModule, TranslatePipe, DateTzPipe, RouterModule],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.css',
})
export class NotificationsDashboard extends BaseView implements OnInit {
  private readonly emailQueueService = inject(EmailQueueService);
  private readonly smsQueueService = inject(SmsQueueService);
  private readonly emailStatusService = inject(EmailStatusService);
  private readonly smsStatusService = inject(SmsStatusService);

  emailStatusMap = signal<Map<string, IEmailStatus>>(new Map());
  smsStatusMap = signal<Map<string, ISmsStatus>>(new Map());

  isLoading = signal<boolean>(true);

  stats = signal<IDashboardStats>({
    emailPending: 0,
    smsPending: 0,
    failedToday: 0,
    deliveredToday: 0,
  });

  recentEmails = signal<IEmailQueue[]>([]);
  recentSms = signal<ISmsQueue[]>([]);

  ngOnInit(): void {
    this.loadDashboardData();
  }

  private async loadDashboardData(): Promise<void> {
    this.isLoading.set(true);
    try {
      const [
        emailStatsRes,
        smsStatsRes,
        recentEmailsRes,
        recentSmsRes,
        emailStatusRes,
        smsStatusRes,
      ] = await Promise.allSettled([
        firstValueFrom(
          this.emailQueueService.count(),
        ) as Promise<IEmailQueueStats>,
        firstValueFrom(this.smsQueueService.count()) as Promise<ISmsQueueStats>,
        firstValueFrom(
          this.emailQueueService.list({
            pagination: { skip: 0, take: 10 },
            sort: [
              { field: 'createdAt', order: ListRequestSortOrderEnum.Desc },
            ],
          }),
        ) as Promise<IPaginatedResponse<IEmailQueue>>,
        firstValueFrom(
          this.smsQueueService.list({
            pagination: { skip: 0, take: 10 },
            sort: [
              { field: 'createdAt', order: ListRequestSortOrderEnum.Desc },
            ],
          }),
        ) as Promise<IPaginatedResponse<ISmsQueue>>,
        firstValueFrom(this.emailStatusService.list({})) as Promise<
          IPaginatedResponse<IEmailStatus>
        >,
        firstValueFrom(this.smsStatusService.list({})) as Promise<
          IPaginatedResponse<ISmsStatus>
        >,
      ]);

      const emailStats =
        emailStatsRes.status === 'fulfilled' ? emailStatsRes.value : null;
      const smsStats =
        smsStatsRes.status === 'fulfilled' ? smsStatsRes.value : null;
      const recentEmailsResult =
        recentEmailsRes.status === 'fulfilled' ? recentEmailsRes.value : null;
      const recentSmsResult =
        recentSmsRes.status === 'fulfilled' ? recentSmsRes.value : null;
      const emailStatusResult =
        emailStatusRes.status === 'fulfilled' ? emailStatusRes.value : null;
      const smsStatusResult =
        smsStatusRes.status === 'fulfilled' ? smsStatusRes.value : null;

      [
        emailStatsRes,
        smsStatsRes,
        recentEmailsRes,
        recentSmsRes,
        emailStatusRes,
        smsStatusRes,
      ].forEach((res) => {
        if (res.status === 'rejected') {
          this.processError(res.reason);
        }
      });

      this.recentEmails.set(recentEmailsResult?.items ?? []);
      this.recentSms.set(recentSmsResult?.items ?? []);

      const emailMap = new Map<string, IEmailStatus>();
      (emailStatusResult?.items ?? []).forEach((s) => emailMap.set(s.code, s));
      this.emailStatusMap.set(emailMap);

      const smsMap = new Map<string, ISmsStatus>();
      (smsStatusResult?.items ?? []).forEach((s) => smsMap.set(s.code, s));
      this.smsStatusMap.set(smsMap);

      this.stats.set({
        emailPending: emailStats?.pending ?? 0,
        smsPending: smsStats?.pending ?? 0,
        failedToday:
          (emailStats?.failedToday ?? 0) + (smsStats?.failedToday ?? 0),
        deliveredToday:
          (emailStats?.sentToday ?? 0) + (smsStats?.deliveredToday ?? 0),
      });
    } finally {
      this.isLoading.set(false);
    }
  }

  getEmailStatusColor(statusCode: string): string {
    return this.emailStatusMap().get(statusCode)?.bgColor || '#f0f0f0';
  }

  getEmailStatusFgColor(statusCode: string): string {
    return this.emailStatusMap().get(statusCode)?.fgColor || '#333';
  }

  getSmsStatusColor(statusCode: string): string {
    return this.smsStatusMap().get(statusCode)?.bgColor || '#f0f0f0';
  }

  getEmailStatusName(statusCode: string): string {
    return this.emailStatusMap().get(statusCode)?.name || statusCode;
  }

  getSmsStatusName(statusCode: string): string {
    return this.smsStatusMap().get(statusCode)?.name || statusCode;
  }

  getSmsStatusFgColor(statusCode: string): string {
    return this.smsStatusMap().get(statusCode)?.fgColor || '#333';
  }

  openEmailDetail(id: string): void {
    this.router.navigate(['/notifications/email-queue', id]);
  }

  openSmsDetail(id: string): void {
    this.router.navigate(['/notifications/sms-queue', id]);
  }

  navigateToEmailQueue(): void {
    this.router.navigate(['/notifications/email-queue']);
  }

  navigateToSmsQueue(): void {
    this.router.navigate(['/notifications/sms-queue']);
  }
}
